import {UserModel} from 'app/models/UserModel'
import {FC, useEffect, useRef} from 'react'
import {shallowEqual, useSelector} from 'react-redux'
import {useLocation, useNavigate} from 'react-router'
import {toast} from 'react-toastify'
import {RootState, UserReducer} from 'setup'
import socket, {setSocketAuthToken} from 'socket'

/**
 * GlobalChatNotificationListener
 */
const GlobalChatNotificationListener: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Using ref to track pathname without re-subscribing
  const pathnameRef = useRef(location.pathname)
  useEffect(() => {
    pathnameRef.current = location.pathname
  }, [location.pathname])

  const userReducer: UserReducer = useSelector<RootState>(
    ({userReducer}) => userReducer,
    shallowEqual
  ) as UserReducer

  const user: UserModel = userReducer.user as UserModel
  const accessToken = userReducer.accessToken

  /*
   * The socket's identity.
   *
   * The server verifies this token and joins `<tenant>:user:<id>` itself, so
   * there is no join-room/leave-room for the user room any more — asking for
   * one by name is refused and logged. This effect is separate from the
   * listener below because it has to re-run when the token is refreshed, not
   * when the user id changes.
   */
  useEffect(() => {
    setSocketAuthToken(accessToken ?? undefined)
  }, [accessToken])

  useEffect(() => {
    if (!user?.id) {
      return
    }

    // Listen for new messages globally
    const handleNewMessage = (data: any) => {
      // Only show notification if:
      // 1. Message is from another user (not self)
      // 2. User is NOT on the messages page
      const isOwnMessage = data.senderId === user.id
      const isOnMessagesPage = pathnameRef.current.startsWith('/messages')

      if (!isOwnMessage && !isOnMessagesPage) {
        const threadName = data.threadName || 'Chat'
        toast.info(`New message from: ${threadName}`, {
          position: 'top-right',
          autoClose: 5000,
          closeButton: true,
          icon: <i className='fa fa-comments text-primary fs-4' />,
          className: 'cursor-pointer',
          onClick: () => navigate(`/messages/${data.threadId}`),
        })
      }
    }

    socket.on('new-message', handleNewMessage)

    return () => {
      socket.off('new-message', handleNewMessage)
    }
  }, [user?.id, navigate])

  return null
}

export default GlobalChatNotificationListener
